import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { z } from 'zod';
import { permissions } from 'shared/constants/permissions';
import { useCopilot } from 'shared/context/CopilotContext';
import { useCopilotFrontendTool, zodToolSchema } from '../hooks/useCopilotFrontendTool';
import { useIsCopilotStandaloneChatPage } from '../hooks/useIsCopilotStandaloneChatPage';
import type { CopilotPageContext, CopilotPageElement, InspectCurrentPageResult } from '../pageContext/types';
import { toCopilotAgentPageContext } from '../pageContext/toCopilotAgentPageContext';
interface InspectCurrentPageToolParams {
  elementId?: string;
}

/**
 * Formats registered page elements as an id:label list for inspect tool error messages.
 * Helps the agent recover when an unknown `elementId` is requested.
 */
const formatAvailableElements = (result: InspectCurrentPageResult): string => {
  if (result.availableElements == null || result.availableElements.length === 0) return 'No inspectable elements registered.';
  return result.availableElements.map(element => `${element.id}: ${element.label}`).join(', ');
};

/**
 * Expands a page element so inspect results include full details instead of compact summaries.
 * Promotes `inspectDetails` to `details` when present.
 */
const expandElementForInspection = (element: CopilotPageElement): CopilotPageElement => {
  const {
    inspectDetails: _inspectDetails,
    ...compactElement
  } = element;
  return {
    ...compactElement,
    details: element.inspectDetails ?? element.details
  };
};

/**
 * Reads the page context `isDataLoaded` flag when the page exposes it as a boolean.
 * Undefined when the page has not published a data-loaded state.
 */
const getPageIsDataLoaded = (pageContext: CopilotPageContext): boolean | undefined => {
  const value = pageContext.state?.isDataLoaded;
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * Registers the `inspect_current_page` frontend tool for reading live page context.
 *
 * Returns compact page context and available element ids, expands a single element
 * when requested, and surfaces `isDataLoaded` for the data-wait wrapper.
 */
export const useInspectCurrentPageTool = () => {
  const $ = _c(11);
  const {
    currentPageContext
  } = useCopilot();
  const isOnCopilotStandaloneChatPage = useIsCopilotStandaloneChatPage();
  let t0;
  bb0: {
    if (currentPageContext == null) {
      t0 = "No page context has been registered yet.";
      break bb0;
    }
    t0 = `${currentPageContext.label}: ${currentPageContext.summary}`;
  }
  const compactPageSummary = t0;
  const t1 = `Inspect the Alvie page the user is currently viewing. Current page summary: ${compactPageSummary}. Use this before answering questions that depend on visible page state, or pass elementId to inspect a specific registered page element.`;
  const t2 = isOnCopilotStandaloneChatPage ? "disabled" : "enabled";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = zodToolSchema(z.object({
      elementId: z.string().describe("Optional id of a specific page element to inspect. Omit this to inspect the whole current page.").optional()
    }));
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== currentPageContext) {
    t4 = params => {
      if (currentPageContext == null) {
        return {
          success: false,
          message: "No page context is currently registered."
        };
      }
      const availableElements = currentPageContext.elements?.map(_temp);
      if (params.elementId != null && params.elementId.trim().length > 0) {
        const element = currentPageContext.elements?.find(item => item.id === params.elementId);
        if (element == null) {
          return {
            success: false,
            message: `Unknown page element "${params.elementId}". Available elements: ${formatAvailableElements({
              success: true,
              message: "",
              availableElements
            })}`,
            isDataLoaded: getPageIsDataLoaded(currentPageContext),
            page: toCopilotAgentPageContext(currentPageContext),
            availableElements
          };
        }
        return {
          success: true,
          message: `Inspected ${element.label}.`,
          isDataLoaded: getPageIsDataLoaded(currentPageContext),
          page: toCopilotAgentPageContext(currentPageContext),
          element: expandElementForInspection(element),
          availableElements
        };
      }
      return {
        success: true,
        message: `Inspected current page: ${currentPageContext.label}.`,
        isDataLoaded: getPageIsDataLoaded(currentPageContext),
        page: toCopilotAgentPageContext(currentPageContext),
        availableElements
      };
    };
    $[1] = currentPageContext;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t4) {
    t5 = {
      name: "inspect_current_page",
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      description: t1,
      available: t2,
      parameters: t3,
      handler: t4
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== compactPageSummary || $[8] !== currentPageContext || $[9] !== isOnCopilotStandaloneChatPage) {
    t6 = [compactPageSummary, currentPageContext, isOnCopilotStandaloneChatPage];
    $[7] = compactPageSummary;
    $[8] = currentPageContext;
    $[9] = isOnCopilotStandaloneChatPage;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useCopilotFrontendTool(t5, t6);
};
function _temp(t0) {
  const {
    id,
    label,
    description
  } = t0;
  return {
    id,
    label,
    description
  };
}